/* eslint-disable @typescript-eslint/no-unused-vars */
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import * as React from "react";
import EditIcon from "@mui/icons-material/Edit";
import CameraAltIcon from "@mui/icons-material/CameraAlt";
import ProgressButton from "../../components/Common/ProgressButton";
import {
  AllCompanyAllowedSpecialPermissions,
  IUser,
} from "../../../types/user";
import userService from "../../../service/user.service";
import UserRoleSelect from "./UserRoleSelect";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import FreeRatioCropper from "../../components/Common/FreeRatioCropper";
import CompanyAutoComplete from "../../components/Common/CompanyAutoComplete";
import { useAppContext } from "../../../contexts/useStorageContext";
import { UserRoles } from "../../../types/auth";
import Chip from "@mui/material/Chip";
import SpecialPermissionPopover from "../../components/User/SpecialPermissionPopover";
import { actionTypes } from "../../../types/state";
import { ICompany } from "../../../types/company";
import { useTranslation } from "react-i18next";

export default function UserDetailForm({
  user,
  onNewCreated,
  onUpdated,
  clientCompany,
}: {
  user?: IUser;
  onNewCreated?: (created: IUser) => void;
  onUpdated?: (updates: IUser) => void;
  clientCompany?: ICompany;
}) {
  const { t } = useTranslation();
  const { state, dispatch } = useAppContext();
  const { myCompanyProfile, loggedInUserInfo } = state;
  const [editingUser, setEditingUser] = React.useState<IUser>({
    approved: true,
    company:
      loggedInUserInfo?.role == UserRoles.superadmin
        ? undefined
        : clientCompany || myCompanyProfile,
  });
  const [processing, setProcessing] = React.useState(false);
  const [openImageUploadDialog, setOpenImageUploadDialog] =
    React.useState(false);

  const handleCreate = async () => {
    setProcessing(true);
    const result = await userService.addNewUser({
      fullname: editingUser.fullname,
      email: editingUser.email,
      role: editingUser.role,
      phone: editingUser.phone,
      approved: editingUser.approved,
      emailNotificationOff: editingUser.emailNotificationOff,
      companyId: editingUser.company?._id,
      clientId: clientCompany?._id,
    });
    if (result?.data) {
      onNewCreated &&
        onNewCreated({ ...result.data, company: editingUser.company });
    }
    setProcessing(false);
  };

  const handleUpdate = async () => {
    try {
      setProcessing(true);
      const result = await userService.updateUser(editingUser);
      if (result?.data) {
        onUpdated && onUpdated(editingUser);
      }
      setProcessing(false);
      if (editingUser?._id == loggedInUserInfo?._id) {
        dispatch!({
          type: actionTypes.setLoggedInUserInfo,
          value: {
            ...(loggedInUserInfo || {}),
            ...editingUser,
            company: editingUser.company,
            authToken: result?.data?.updatedToken,
          },
        });
        dispatch!({
          type: actionTypes.setMyCompanyProfile,
          value: editingUser.company,
        });
      }
    } catch (error) {
      console.error(error);
    } finally {
      setProcessing(false);
    }
  };

  React.useEffect(() => {
    if (user) {
      setEditingUser(user);
    }
  }, [user]);
  return (
    <Box>
      <Stack spacing={3}>
        <Box
          sx={{
            position: "relative",
            width: "150px",
            height: "150px",
            backgroundColor: (theme) =>
              theme.palette.mode === "dark" ? "#152033" : "#f3f3f3",
            borderRadius: "5px",
            backgroundImage: `url(${editingUser.avatar || ""})`,
            backgroundRepeat: "no-repeat",
            backgroundSize: "contain",
            backgroundPosition: "center",
          }}
        >
          <IconButton
            sx={{
              position: "absolute",
              top: 10,
              left: 10,
              backgroundColor: "#0003",
              color: "#fff",
              "&:hover": { backgroundColor: "primary.main", color: "#fff" },
            }}
            onClick={() => setOpenImageUploadDialog(true)}
          >
            <CameraAltIcon />
          </IconButton>
        </Box>
        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 3, sm: 2 }}
        >
          <TextField
            value={editingUser.fullname || ""}
            onChange={(e) =>
              setEditingUser({ ...editingUser, fullname: e.target.value })
            }
            label={t("Full Name")}
            fullWidth
            size="small"
          />
          <TextField
            value={editingUser.email || ""}
            onChange={(e) =>
              setEditingUser({ ...editingUser, email: e.target.value })
            }
            label={t("Email")}
            fullWidth
            size="small"
          />
        </Stack>

        <Stack
          direction={{ xs: "column", sm: "row" }}
          spacing={{ xs: 3, sm: 2 }}
        >
          <Box sx={{ flex: 1 }}>
            <CompanyAutoComplete
              fullWidth
              value={editingUser.company}
              onChange={(selected) =>
                setEditingUser({
                  ...editingUser,
                  company: selected || undefined,
                })
              }
            />
          </Box>
          <Box sx={{ flex: 1 }}>
            <UserRoleSelect
              label={t("Role")}
              value={editingUser.role}
              onChange={(newVal) =>
                setEditingUser({ ...editingUser, role: newVal })
              }
              currentUserRole={loggedInUserInfo?.role}
              isEditingOwnProfile={user?._id === loggedInUserInfo?._id}
            />
          </Box>
        </Stack>
        {loggedInUserInfo?.role &&
        [UserRoles.superadmin, UserRoles.companyAdmin].includes(
          loggedInUserInfo?.role
        ) ? (
          <Box sx={{ display: "flex", alignItems: "center", flexWrap: "wrap" }}>
            <div style={{ marginRight: "8px" }}>
              {t("Special Permissions")}:
            </div>
            {editingUser.permissions
              ?.filter(
                (p) =>
                  AllCompanyAllowedSpecialPermissions?.includes(
                    p.permissionName
                  ) ||
                  UserRoles.superadmin == loggedInUserInfo?.role ||
                  user?.company?.permissions
                    ?.map((cp) => cp.permissionName)
                    ?.includes(p.permissionName)
              )
              .map((permissionStatus) => (
                <Chip
                  sx={{ mr: "4px", my: "2px" }}
                  label={`${
                    permissionStatus.permissionName
                  }: ${permissionStatus.limitedPermissions?.join(", ")||""}${
                    permissionStatus.limitedPermissions?.length ? " / " : ""
                  }${permissionStatus.read ? t("Read") : ""} ${
                    permissionStatus.write ? " / " + t("write") : ""
                  }`}
                />
              ))}
            <SpecialPermissionPopover
              targetCompany={user?.company}
              targetUser={user}
              triggerElement={
                <IconButton size="small">
                  <EditIcon />
                </IconButton>
              }
              currentPermissions={editingUser.permissions || []}
              onChangePermissions={(changed) =>
                setEditingUser({ ...editingUser, permissions: changed })
              }
            />
          </Box>
        ) : null}
      </Stack>
      <Box mt={3} sx={{ display: "flex", alignItems: "center" }}>
        {loggedInUserInfo?.role &&
        [UserRoles.superadmin, UserRoles.companyAdmin].includes(
          loggedInUserInfo?.role
        ) ? (
          <FormControlLabel
            control={
              <Switch
                checked={editingUser.approved}
                onChange={(e, checked) =>
                  setEditingUser({ ...editingUser, approved: checked })
                }
              />
            }
            label={t("Approved Status")}
          />
        ) : null}
        <FormControlLabel
          control={
            <Switch
              checked={editingUser.emailNotificationOff !== true}
              onChange={(_e, checked) =>
                setEditingUser({
                  ...editingUser,
                  emailNotificationOff: !checked,
                })
              }
            />
          }
          label={t("Email Notification")}
        />

        <div style={{ flex: 1 }} />

        {user ? (
          <ProgressButton
            onClick={() => !processing && handleUpdate()}
            title={t("Save Changes")}
            inProgress={processing}
          />
        ) : (
          <ProgressButton
            onClick={() => !processing && handleCreate()}
            title={t("Create User")}
            inProgress={processing}
          />
        )}
      </Box>
      {openImageUploadDialog && (
        <FreeRatioCropper
          ratio={1}
          onSave={(url) => {
            setEditingUser({ ...editingUser, avatar: url });
            setOpenImageUploadDialog(false);
          }}
          open
          onClose={() => setOpenImageUploadDialog(false)}
        />
      )}
    </Box>
  );
}
